import type {HeadcountRollup, WireframeReportSpend} from 'client';

export const EMPTY_VALUE = '—';

type HeadcountField = keyof HeadcountRollup;
type SpendField = keyof WireframeReportSpend;

export type WireframeCategory = {
  key: 'growth' | 'ci' | 'ktlo';
  label: string;
  shortLabel: string;
  headcount: HeadcountField;
  spend: SpendField;
};

// The report's fixed series order: each category keeps its color everywhere.
export const WIREFRAME_CATEGORIES: readonly WireframeCategory[] = [
  {
    key: 'growth',
    label: 'Growth',
    shortLabel: 'Growth',
    headcount: 'allocation_growth_fte',
    spend: 'allocation_growth_usd',
  },
  {
    key: 'ci',
    label: 'Continuous Improvement',
    shortLabel: 'CI',
    headcount: 'allocation_ci_fte',
    spend: 'allocation_ci_usd',
  },
  {
    key: 'ktlo',
    label: 'KTLO',
    shortLabel: 'KTLO',
    headcount: 'allocation_ktlo_fte',
    spend: 'allocation_ktlo_usd',
  },
];

export type WireframeGeo = {
  key: 'low' | 'mid' | 'high';
  label: string;
  shortLabel: string;
  headcount: HeadcountField;
  spend: SpendField;
};

export const WIREFRAME_GEOS: readonly WireframeGeo[] = [
  {
    key: 'low',
    label: 'Low cost',
    shortLabel: 'Low',
    headcount: 'geo_low_fte',
    spend: 'geo_low_usd',
  },
  {
    key: 'mid',
    label: 'Mid cost',
    shortLabel: 'Mid',
    headcount: 'geo_mid_fte',
    spend: 'geo_mid_usd',
  },
  {
    key: 'high',
    label: 'High cost',
    shortLabel: 'High',
    headcount: 'geo_high_fte',
    spend: 'geo_high_usd',
  },
];

/** The report's decimal strings as numbers; anything unparseable counts as 0. */
export const toNumber = (value: string | null | undefined) => {
  const parsed = Number(value);
  return value != null && Number.isFinite(parsed) ? parsed : 0;
};

const headcountFormat = new Intl.NumberFormat('en-US', {maximumFractionDigits: 1});
const usdFormat = new Intl.NumberFormat('en-US', {
  style: 'currency',
  currency: 'USD',
  notation: 'compact',
  // Currency otherwise keeps its cents' minimum: "$740.0K".
  minimumFractionDigits: 0,
  maximumFractionDigits: 1,
});

/** FTE to one decimal place, without a trailing ".0": 282, 12.5. */
export const formatHeadcount = (value: string | number) => {
  const formatted = headcountFormat.format(typeof value === 'number' ? value : toNumber(value));
  // A tiny negative rounds to "-0".
  return formatted === '-0' ? '0' : formatted;
};

/** Compact USD: $59.9M, $740K, $0. */
export const formatUsd = (value: string | number) =>
  usdFormat.format(typeof value === 'number' ? value : toNumber(value));

/** A 0–1 ratio as a whole percent; a nonzero share that rounds to 0 reads "<1%". */
export const formatShare = (ratio: string | number | null | undefined) => {
  if (ratio == null || ratio === '') return EMPTY_VALUE;
  const value = typeof ratio === 'number' ? ratio : Number(ratio);
  if (!Number.isFinite(value)) return EMPTY_VALUE;
  const percent = value * 100;
  if (percent > 0 && percent < 0.5) return '<1%';
  return `${Math.round(percent)}%`;
};

/** part ÷ whole, or null where the ratio is undefined. */
export const shareOf = (part: number, whole: number) => (whole > 0 ? part / whole : null);
